import React, { useState, useEffect } from 'react';
import { ClockCounterClockwise, CalendarBlank, CheckCircle, XCircle, Eye } from '@phosphor-icons/react';
import GenericTables from '../../../shared/components/ui/GenericTables.jsx';
import GenericFilters from '../../../shared/components/ui/GenericFilters.jsx';
import { fetchTaskHistory } from '../../../services/technicianService.js';
import TaskDetailModal from '../components/modals/TaskDetailModal.jsx';
import { getTaskStatusBadgeStyles, getTaskStatusLabel } from '../../../constants/taskColors.js';

/**
 * Task History View Component
 * Displays a list of completed and cancelled tasks using generic components
 */
const TaskHistoryView = () => {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        status: '',
        startDate: '',
        endDate: '',
        clientName: '',
        taskTypeId: '',
        validationStatus: ''
    });

    // Estado para el modal de detalles
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);

    useEffect(() => {
        loadHistoricalTasks();
    }, [filters]);

    const loadHistoricalTasks = async () => {
        try {
            setLoading(true);

            // Construir filtros solo con valores no vacíos
            const activeFilters = {};
            if (filters.status) activeFilters.status = filters.status;
            if (filters.startDate) activeFilters.startDate = filters.startDate;
            if (filters.endDate) activeFilters.endDate = filters.endDate;
            if (filters.clientName) activeFilters.clientName = filters.clientName;
            if (filters.taskTypeId) activeFilters.taskTypeId = filters.taskTypeId;

            const data = await fetchTaskHistory(activeFilters);
            let nextTasks = Array.isArray(data?.tasks)
                ? data.tasks
                : Array.isArray(data)
                    ? data
                    : [];

            if (filters.validationStatus) {
                nextTasks = nextTasks.filter(task => {
                    if (filters.validationStatus === 'no_solicitada') {
                        return !task.validationRequest;
                    }
                    return task.validationRequest?.status === filters.validationStatus;
                });
            }

            setTasks(nextTasks);
        } catch (error) {
            console.error('Error fetching historical tasks:', error);
            setTasks([]);
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        return (
            <span 
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                style={getTaskStatusBadgeStyles(status)}
            >
                {getTaskStatusLabel(status)}
            </span>
        );
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-AR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-AR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Configuración de columnas de filtros
    const filterColumns = [
        {
            type: 'select',
            key: 'status',
            label: 'Estado',
            colSpan: 1,
            options: [
                { id: '', name: 'Todos' },
                { id: 'finalizada', name: 'Finalizada' },
                { id: 'cancelada', name: 'Cancelada' }
            ],
            optionValue: 'id',
            optionLabel: 'name'
        },
        {
            type: 'date',
            key: 'startDate',
            label: 'Fecha Desde',
            colSpan: 1
        },
        {
            type: 'date',
            key: 'endDate',
            label: 'Fecha Hasta',
            colSpan: 1
        },
        {
            type: 'text',
            key: 'clientName',
            label: 'Cliente',
            colSpan: 1,
            placeholder: 'Buscar cliente...'
        },
        {
            type: 'select',
            key: 'validationStatus',
            label: 'Validación',
            colSpan: 1,
            options: [
                { id: '', name: 'Todos' },
                { id: 'pendiente', name: 'Pendiente' },
                { id: 'validada', name: 'Validada' },
                { id: 'no_solicitada', name: 'No solicitada' }
            ],
            optionValue: 'id',
            optionLabel: 'name'
        }
    ];

    // Configuración de columnas de la tabla
    const tableColumns = [
        {
            key: 'title',
            label: 'Tarea',
            align: 'left',
            render: (row) => (
                <div className="flex flex-col">
                    <span className="font-semibold text-content">{row.title}</span>
                    <span className="text-xs text-content-muted">{row.taskTypeId || 'N/A'}</span>
                </div>
            )
        },
        {
            key: 'client',
            label: 'Cliente',
            align: 'left',
            render: (row) => (
                <div className="flex flex-col">
                    <span className="text-content">{row.client?.name || 'N/A'}</span>
                    <span className="text-xs text-content-muted">{row.client?.address || ''}</span>
                </div>
            )
        },
        {
            key: 'ticketNumber',
            label: 'ID Ticket',
            align: 'left',
            render: (row) => (
                <div className="flex flex-col">
                    <span className="text-content">{row.ticketNumber || 'N/A'}</span>
                </div>
            )
        },
        {
            key: 'scheduledDate',
            label: 'Fecha Programada',
            align: 'left',
            render: (row) => (
                <div className="flex items-center gap-2">
                    <CalendarBlank className="w-4 h-4 text-content-muted" />
                    <span>{formatDate(row.scheduledDate)}</span>
                </div>
            )
        },
        {
            key: 'completedAt',
            label: 'Fecha Finalización',
            align: 'left',
            render: (row) => (
                <div className="flex items-center gap-2">
                    {row.completedAt ? (
                        <>
                            <CheckCircle className="w-4 h-4 text-success" />
                            <span>{formatDateTime(row.completedAt)}</span>
                        </>
                    ) : (
                        <span className="text-muted-deep">-</span>
                    )}
                </div>
            )
        },
        {
            key: 'status',
            label: 'Estado',
            align: 'center',
            render: (row) => getStatusBadge(row.status)
        },
        {
            key: 'validation',
            label: 'Validación',
            align: 'center',
            render: (row) => {
                if (!row.validationRequest) return <span className="text-muted-deep text-sm">No solicitada</span>;
                const status = row.validationRequest.status;
                const colors = {
                    validada: 'text-success',
                    pendiente: 'text-warning'
                };
                const colorClass = colors[status] || 'text-content-muted';
                return (
                    <span className={`text-sm ${colorClass}`}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                    </span>
                );
            }
        },
        {
            key: 'actions',
            label: 'Acciones',
            align: 'center',
            isAction: true,
            render: (row) => (
                <button
                    onClick={() => handleViewDetails(row)}
                    className="flex items-center justify-center gap-2 px-4 py-2 rounded bg-amber hover:bg-amber-hover text-on-amber transition-colors w-full md:w-auto"
                    title="Ver detalles"
                >
                    <Eye size={18} weight="bold" />
                    <span className="md:hidden text-sm font-semibold">Ver Detalles</span>
                </button>
            )
        }
    ];

    const handleViewDetails = (task) => {
        setSelectedTask(task);
        setShowDetailModal(true);
    };

    const handleCloseDetailModal = () => {
        setShowDetailModal(false);
        setSelectedTask(null);
    };

    const handleFilterChange = (newFilters) => {
        const nextFilters = { ...newFilters };
        const { startDate, endDate } = nextFilters;

        if (startDate && endDate && endDate < startDate) {
            nextFilters.startDate = endDate;
            nextFilters.endDate = startDate;
        }

        setFilters(nextFilters);
    };




    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center space-x-3">
                    <div className="p-3 bg-amber/12 rounded-lg">
                        <ClockCounterClockwise className="w-6 h-6 text-accent-text" weight="fill" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-content">Historial de Tareas</h2>
                        <p className="text-content-muted text-sm">
                            {tasks.length} {tasks.length === 1 ? 'tarea' : 'tareas'}
                        </p>
                    </div>
                </div>
            </div>

            {/* Filtros */}
            <GenericFilters
                columns={filterColumns}
                filters={filters}
                onFilterChange={handleFilterChange}
                defaultOpen={false}
            />

            {/* Tabla de tareas con loading overlay */}
            <div className="relative rounded-xl shadow-md overflow-hidden">
                <GenericTables
                    columns={tableColumns}
                    data={tasks}
                    emptyMessage="No hay tareas en el historial"
                    zebraStripe={true}
                    mobileCardVariant="legacy"
                />
                {loading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-[var(--app-overlay)] z-10">
                        <div className="flex items-center gap-3 rounded-lg border border-line bg-surface/90 px-4 py-3 text-sm text-content shadow-lg">
                            <span className="h-5 w-5 animate-spin rounded-full border-2 border-amber border-t-transparent" />
                            Cargando historial...
                        </div>
                    </div>
                )}
            </div>

            {/* Modal de detalles */}
            <TaskDetailModal
                task={selectedTask}
                isOpen={showDetailModal}
                onClose={handleCloseDetailModal}
            />
        </div>
    );
};

export default TaskHistoryView;

